import axios from 'axios';
import { handleSessionEnded, isSessionEnded } from './sessionEnded';
import { deviceId } from './deviceId';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ⚠️ A SESSION THE SERVER HAS ENDED (BUGS.md S1) — archived, made an employee, signed out by a
// password change, or seven days up — sends the app to the sign-in page saying why, instead of
// a red error on every click. `lib/sessionEnded.js` has the rules. On EVERY axios the app uses:
// the shared one (several pages call it directly) and each client made below, which does not
// inherit the shared one's watchers.
const watchForEndedSessions = (instance) => {
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (isSessionEnded(error)) {
        handleSessionEnded({
          storage: { local: window.localStorage, session: window.sessionStorage },
          go: (path) => window.location.assign(path),
        });
      }
      return Promise.reject(error);
    },
  );
  return instance;
};
watchForEndedSessions(axios);

// Which browser this is, on every request — so the server can say "New device" without
// fingerprinting anybody (`lib/deviceId.js`, DESIGN-security.md). Set on the shared axios's
// defaults, which every client made below copies.
try {
  const _device = deviceId();
  if (_device) axios.defaults.headers.common['X-HH-Device'] = _device;
} catch { /* no device number: nothing is marked "new" */ }

// Create axios instance with auth header
const createApiClient = () => {
  const token = localStorage.getItem('token');
  return watchForEndedSessions(axios.create({
    baseURL: `${API_URL}/api`,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  }));
};

// Turn any axios/FastAPI error into a DISPLAYABLE STRING. FastAPI validation
// errors (HTTP 422) arrive as an ARRAY of objects under `detail`; handing that
// straight to a toast makes React try to render an object and blanks the whole
// page (React error #31). Always reduce it to a string here.
export const apiErrorMessage = (e, fallback = 'Something went wrong') => {
  const d = e && e.response && e.response.data && e.response.data.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) {
    const msgs = d.map((x) => (x && x.msg) || (typeof x === 'string' ? x : null)).filter(Boolean);
    return msgs.length ? msgs.join('; ') : fallback;
  }
  if (d && typeof d === 'object') return d.msg || fallback;
  return (e && typeof e.message === 'string') ? e.message : fallback;
};

// Auth API
export const authApi = {
  login: (email, password) => 
    axios.post(`${API_URL}/api/auth/login`, { email, password }),
  register: (data) => 
    axios.post(`${API_URL}/api/auth/register`, data),
  getMe: () => 
    createApiClient().get('/auth/me'),
  forgotPassword: (email) =>
    axios.post(`${API_URL}/api/auth/forgot-password`, { email }),
  resetPassword: (token, password) =>
    axios.post(`${API_URL}/api/auth/reset-password`, { token, password }),
  verifyEmail: (token) =>
    axios.post(`${API_URL}/api/auth/verify-email`, { token }),
  resendVerification: (email) =>
    axios.post(`${API_URL}/api/auth/resend-verification`, { email }),
  // Sign in with a six-digit emailed code — employees only (DESIGN-signing-in.md H/H-bis).
  // ⚠️ requestLoginCode ALWAYS resolves with the same message whether or not the address
  // can actually receive one. Do not add error handling that distinguishes the cases; the
  // sameness is the security property.
  requestLoginCode: (email) =>
    axios.post(`${API_URL}/api/auth/request-code`, { email }),
  codeLogin: (email, code) =>
    axios.post(`${API_URL}/api/auth/code-login`, { email, code }),
  // Two-factor. ⚠️ `twofactorVerify` is deliberately NOT authenticated — the caller has no
  // session yet, that is the whole point. It carries the scoped pending token instead,
  // which the backend refuses to accept as a session anywhere else.
  twofactorVerify: (twofactor_token, code) =>
    axios.post(`${API_URL}/api/auth/2fa/verify`, { twofactor_token, code }),
  twofactorResend: (twofactor_token) =>
    axios.post(`${API_URL}/api/auth/2fa/resend`, { twofactor_token }),
  twofactorStatus: () =>
    createApiClient().get('/auth/2fa/status'),
  twofactorStart: (method) =>
    createApiClient().post('/auth/2fa/start', { method }),
  twofactorConfirm: (code) =>
    createApiClient().post('/auth/2fa/confirm', { code }),
  twofactorDisable: (password) =>
    createApiClient().post('/auth/2fa/disable', { password }),
  twofactorRegenerateCodes: (password) =>
    createApiClient().post('/auth/2fa/recovery-codes', { password }),
  // Forced enrolment after the grace period ran out (G8-bis). ⚠️ Deliberately NOT
  // authenticated — the caller is blocked from signing in, that is why they are here.
  // The scoped set-up token from login() proves their password; the backend refuses it
  // as a session everywhere else. Confirming returns a real session.
  twofactorSetupStart: (twofactor_setup_token, method) =>
    axios.post(`${API_URL}/api/auth/2fa/setup/start`, { twofactor_setup_token, method }),
  twofactorSetupConfirm: (twofactor_setup_token, code) =>
    axios.post(`${API_URL}/api/auth/2fa/setup/confirm`, { twofactor_setup_token, code }),
  // Public: what the sign-up page needs before anyone has an account. The trial
  // length here is the SAME number register() will actually give them.
  signupInfo: () =>
    axios.get(`${API_URL}/api/auth/signup-info`),
};

// ── The /welcome gate ────────────────────────────────────────────────────────────────
//
// Sign-up now asks only for an email and a password; the company name and the person's
// name are asked here, before the confirmation email goes out, so it can greet them
// properly. See routers/onboarding.py::update_essentials.
//
// ⚠️ THE TOKEN IS PASSED EXPLICITLY AND NEVER STORED AS `token`. Mid-sign-up the caller
// holds a SCOPED token that can do this one thing and nothing else. Putting it in
// localStorage under `token` would make the whole app believe somebody is signed in, and
// would send a token to every endpoint that every endpoint is built to refuse. Once they
// are properly logged in (the safety-net route), no token is passed and the normal
// authenticated client is used instead.
// ── Signing in with Google or Microsoft ──────────────────────────────────────────────
//
// ⚠️ `startUrl` is a REAL BROWSER NAVIGATION, not a fetch. The provider redirects the
// browser to its own sign-in page and back again; an XHR cannot do that, and trying
// produces a CORS error that looks like a bug in us.
//
// ⚠️ The code that comes back is single-use and short-lived, and does nothing on its own —
// it must be POSTed back to be swapped for a session. That is why the session token itself
// never appears in a URL. See routers/sso.py.
export const ssoApi = {
  providers: () => axios.get(`${API_URL}/api/auth/sso/providers`),
  startUrl: (provider) => `${API_URL}/api/auth/${provider}/login`,
  exchange: (code) => axios.post(`${API_URL}/api/auth/sso/exchange`, { code }),
  complete: (code) => axios.post(`${API_URL}/api/auth/sso/complete`, { code }),
};

export const essentialsApi = {
  get: (signupToken) => (signupToken
    ? axios.get(`${API_URL}/api/onboarding/essentials`,
        { headers: { Authorization: `Bearer ${signupToken}` } })
    : createApiClient().get('/onboarding/essentials')),

  save: (data, signupToken) => (signupToken
    ? axios.put(`${API_URL}/api/onboarding/essentials`, data,
        { headers: { Authorization: `Bearer ${signupToken}` } })
    : createApiClient().put('/onboarding/essentials', data)),
};

// Email API (admin / superadmin) — config status + test send
export const emailApi = {
  getStatus: () => createApiClient().get('/email/status'),
  sendTest: (recipient_email, subject, message) =>
    createApiClient().post('/email/test', { recipient_email, subject, message }),
  revealKey: (provider) => createApiClient().post('/email/config/reveal', { provider }),
  saveConfig: (data) => createApiClient().put('/email/config', data),
};

// Users API
export const supportApi = {
  listTickets: () => createApiClient().get('/support/tickets'),
  getTicket: (id) => createApiClient().get(`/support/tickets/${id}`),
  createTicket: (subject, body, accessConsent = false, files = []) => {
    const fd = new FormData();
    fd.append('subject', subject);
    fd.append('body', body || '');
    fd.append('access_consent', accessConsent ? 'true' : 'false');
    (files || []).forEach((f) => fd.append('files', f));
    // Do NOT set Content-Type — the browser sets multipart/form-data WITH the
    // boundary automatically for a FormData body. Setting it by hand omits the
    // boundary, so the server can't parse the form (422).
    return createApiClient().post('/support/tickets', fd);
  },
  reply: (id, body, files = []) => {
    const fd = new FormData();
    fd.append('body', body || '');
    (files || []).forEach((f) => fd.append('files', f));
    return createApiClient().post(`/support/tickets/${id}/messages`, fd);   // browser sets the multipart boundary
  },
  setConsent: (id, consent) => createApiClient().put(`/support/tickets/${id}/consent`, { consent }),
  unreadCount: () => createApiClient().get('/support/unread-count'),
  // Authenticated fetch of an attachment's bytes: the Bearer JWT travels in the
  // header (a bare <img src> can't carry it), so we pull a blob and render it.
  getAttachment: (id) =>
    createApiClient().get(`/support/attachments/${id}`, { responseType: 'blob' }),
};

export const usersApi = {
  list: (params) => 
    createApiClient().get('/users', { params }),
  get: (id) => 
    createApiClient().get(`/users/${id}`),
  create: (data) => 
    createApiClient().post('/users', data),
  update: (id, data) => 
    createApiClient().put(`/users/${id}`, data),
  delete: (id) =>
    createApiClient().delete(`/users/${id}`),
  // NOTE: "delete" above actually ARCHIVES (soft). Restore reverses it.
  restore: (id) =>
    createApiClient().post(`/users/${id}/restore`),
  // The leaver flow (lifecycle Phase B): end-dated archive with settlement
  archivePreview: (id, endDate) =>
    createApiClient().get(`/users/${id}/archive-preview`, { params: { end_date: endDate } }),
  archiveWithDate: (id, body) =>
    createApiClient().post(`/users/${id}/archive`, body),
  cancelScheduledArchive: (id) =>
    createApiClient().post(`/users/${id}/archive-schedule/cancel`),
  // Deletion (lifecycle Phase C): export + anonymise/erase from archived state
  exportUser: (id) =>
    createApiClient().get(`/users/${id}/export`, { responseType: 'blob' }),
  deletePermanently: (id, body) =>
    createApiClient().post(`/users/${id}/delete`, body),
  // Effective-dated schedule changes ("from 1 August, they work these hours")
  // A3 (22/10/2026): answer "days or hours?" for one person — 'days' | 'hours'.
  setLeaveUnit: (userId, unit) =>
    createApiClient().post(`/users/${userId}/leave-unit/${unit}`),
  getSchedule: (userId) =>
    createApiClient().get(`/users/${userId}/schedule`),
  setUpcomingSchedule: (userId, data) =>
    createApiClient().put(`/users/${userId}/schedule/upcoming`, data),
  cancelUpcomingSchedule: (userId) =>
    createApiClient().delete(`/users/${userId}/schedule/upcoming`),
  // Emergency Contacts
  addEmergencyContact: (userId, data) =>
    createApiClient().post(`/users/${userId}/emergency-contacts`, data),
  updateEmergencyContact: (userId, contactId, data) =>
    createApiClient().put(`/users/${userId}/emergency-contacts/${contactId}`, data),
  deleteEmergencyContact: (userId, contactId) =>
    createApiClient().delete(`/users/${userId}/emergency-contacts/${contactId}`),
  // Invite
  sendInvite: (userId) =>
    createApiClient().post(`/users/${userId}/invite`),
  // Copyable password-setup link (admin-only; no email needed — 7-day expiry)
  getInviteLink: (userId) =>
    createApiClient().post(`/users/${userId}/invite-link`),
};

// Departments API
export const departmentsApi = {
  list: () => 
    createApiClient().get('/departments'),
  get: (id) => 
    createApiClient().get(`/departments/${id}`),
  create: (data) => 
    createApiClient().post('/departments', data),
  update: (id, data) => 
    createApiClient().put(`/departments/${id}`, data),
  delete: (id) => 
    createApiClient().delete(`/departments/${id}`),
};

// Locations API
export const locationsApi = {
  list: () => 
    createApiClient().get('/locations'),
  get: (id) => 
    createApiClient().get(`/locations/${id}`),
  create: (data) => 
    createApiClient().post('/locations', data),
  update: (id, data) => 
    createApiClient().put(`/locations/${id}`, data),
  delete: (id) => 
    createApiClient().delete(`/locations/${id}`),
  // Move the head office flag. ⚠️ Clears it from every other location in the same
  // transaction — exactly one, always (decision I).
  // ⚠️ What changes if this becomes the head office — READ-ONLY. Ask this BEFORE
  // calling makeHeadOffice: when the two records disagree the move needs a direction,
  // and the server refuses without one rather than letting the next save decide.
  headOfficePreview: (id) =>
    createApiClient().get(`/locations/${id}/head-office-preview`),
  makeHeadOffice: (id, direction) =>
    createApiClient().post(`/locations/${id}/make-head-office`, { direction }),
  getSettings: () =>
    createApiClient().get('/locations/settings'),
  // The Timezone box's choices (A9b): every real timezone, what "same as the company"
  // means right now, and each country's main timezone for the box's suggestion.
  getTimezoneOptions: () =>
    createApiClient().get('/locations/timezone-options'),
  updateSettings: (data) =>
    createApiClient().put('/locations/settings', data),
  assignEmployees: (assignments) =>
    createApiClient().post('/locations/assign-employees', { assignments }),
  // What a country's working week USUALLY is — to OFFER, never to apply.
  // ⚠️ Nothing may call this and set the days automatically. See
  // backend/services/working_weeks.py for why that matters.
  getWorkingWeekSuggestion: (countryCode) =>
    createApiClient().get(`/locations/working-week-suggestion/${countryCode}`),
  // Who at this location changes if it takes this working week. READ-ONLY — nothing
  // is saved, and it never blocks a save. See backend/services/working_week_preview.py.
  previewWorkingWeek: (id, workingHours) =>
    createApiClient().post(`/locations/${id}/working-week-preview`, { working_hours: workingHours }),
};

// Leave API
export const leaveApi = {
  // Leave types
  getTypes: () => 
    createApiClient().get('/leave/types'),
  createType: (data) => 
    createApiClient().post('/leave/types', data),
  updateType: (id, data) => 
    createApiClient().put(`/leave/types/${id}`, data),
  deleteType: (id) => 
    createApiClient().delete(`/leave/types/${id}`),
  
  // Leave requests
  listRequests: (params) => 
    createApiClient().get('/leave/requests', { params }),
  getRequest: (id) => 
    createApiClient().get(`/leave/requests/${id}`),
  createRequest: (data) =>
    createApiClient().post('/leave/requests', data),
  calculateDays: (data) =>
    createApiClient().post('/leave/calculate-days', data),
  actionRequest: (id, action, reason) => 
    createApiClient().post(`/leave/requests/${id}/action`, { action, reason }),
  cancelRequest: (id) =>
    createApiClient().delete(`/leave/requests/${id}`),
  // The cancel box (DESIGN-part-cancel.md): the booking's days and who may give back which,
  // then giving some back — `preview: true` says what would happen and changes nothing.
  getCancelDays: (id) =>
    createApiClient().get(`/leave/requests/${id}/days`),
  giveBack: (id, days, preview = false) =>
    createApiClient().post(`/leave/requests/${id}/give-back`, { days, preview }),
  // The requester's ONE chase. Server enforces once-ever, after-24h, requester-only —
  // the button is only ever a convenience, never the guard.
  nudge: (id) =>
    createApiClient().post(`/leave/requests/${id}/nudge`),
  // The receipt: "how was this worked out?" (own by default; admins/managers
  // may pass { user_id } for people whose balances they already see)
  explainAllowance: (params) =>
    createApiClient().get('/leave/allowances/explain', { params }),
  // Bookable granularity (company setting; readable by any signed-in user)
  getBookingGranularity: () =>
    createApiClient().get('/leave/booking-granularity'),

  // Allowances
  getMyAllowances: (year) =>
    createApiClient().get('/leave/allowances', { params: { year } }),
  // May I book leave for this person (past days included)? The server's one rule (may_book_for).
  canBookFor: (userId) =>
    createApiClient().get(`/leave/can-book-for/${userId}`),
  getUserAllowances: (userId, year) =>
    createApiClient().get(`/leave/allowances/${userId}`, { params: { year } }),
  // The signed-in person's NEXT leave year: its balance and what is booked in it
  // (DESIGN-leave-years.md decision H, option B).
  getMyNextLeaveYear: () =>
    createApiClient().get('/leave/allowances/next-year'),
  // Leave TAKEN, grouped by leave type, for a user + year. Feeds the "Other
  // leave taken" sidebar breakdown (Sick / Unpaid / WFH etc). Optional
  // { user_id, year } — omit user_id for the signed-in user's own totals.
  getLeaveTakenByType: (params) =>
    createApiClient().get('/leave/taken-by-type', { params }),
};

// "Switch from Timetastic" importer (admin only). Multipart uploads.
// Time in lieu (DESIGN-toil.md, 20/10/2026). Every rule is the server's (services/toil.py);
// these only ask.
export const toilApi = {
  getSettings: () => createApiClient().get('/toil/settings'),
  saveSettings: (data) => createApiClient().put('/toil/settings', data),
  mine: () => createApiClient().get('/toil/me'),
  forUser: (userId) => createApiClient().get(`/toil/users/${userId}`),
  // Your own claim, or (with user_id) a manager or admin recording it directly.
  claim: (data) => createApiClient().post('/toil/claims', data),
  decide: (id, data) => createApiClient().post(`/toil/claims/${id}/decide`, data),
  withdraw: (id) => createApiClient().post(`/toil/claims/${id}/withdraw`),
  pending: () => createApiClient().get('/toil/pending'),
  correct: (data) => createApiClient().post('/toil/corrections', data),
  // The same check the booking route makes, for the booking dialog.
  check: (data) => createApiClient().post('/toil/check', data),
};

export const importApi = {
  previewTimetastic: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return createApiClient().post('/import/timetastic/preview', fd,
      { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  applyTimetastic: (file, options) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('options', JSON.stringify(options || {}));
    return createApiClient().post('/import/timetastic/apply', fd,
      { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

// Dashboard API
export const dashboardApi = {
  getStats: () => 
    createApiClient().get('/dashboard/stats'),
  getWallchart: (startDate, endDate, departmentId) => 
    createApiClient().get('/dashboard/wallchart', { 
      params: { start_date: startDate, end_date: endDate, department_id: departmentId } 
    }),
  getPendingApprovals: () =>
    createApiClient().get('/dashboard/pending-approvals'),
  getApprovalSummary: (userId) =>
    createApiClient().get(`/dashboard/approval-summary/${userId}`),
  getMySetup: () =>
    createApiClient().get('/dashboard/my-setup'),
  // Only the caller's own approved leave — filtered on the server (DESIGN-reports.md 0a).
  getMyUpcomingLeave: (days = 30) =>
    createApiClient().get('/dashboard/my-upcoming-leave', { params: { days } }),
  // "Team time off": exactly what this person can already see on the wallchart, added up.
  getTeamTimeOff: () =>
    createApiClient().get('/dashboard/team-time-off'),
  getApproverChains: () =>
    createApiClient().get('/dashboard/approver-chains'),
};

// Year-end rollover (admin)
export const rolloverApi = {
  preview: (year) => createApiClient().get('/leave/rollover/preview', { params: year ? { year } : {} }),
  run: (year) => createApiClient().post('/leave/rollover/run', year ? { year } : {}),
  undo: (year) => createApiClient().delete(`/leave/rollover/${year}`),
};

// Email templates (superadmin)
export const emailTemplatesApi = {
  list: () => createApiClient().get('/email-templates'),
  get: (key) => createApiClient().get(`/email-templates/${key}`),
  save: (key, data) => createApiClient().put(`/email-templates/${key}`, data),
  reset: (key) => createApiClient().post(`/email-templates/${key}/reset`),
  preview: (key, data) => createApiClient().post(`/email-templates/${key}/preview`, data),
  test: (key, data) => createApiClient().post(`/email-templates/${key}/test`, data),
};

// Reports (DESIGN-reports.md). Every one of these needs the "Can view reports" permission
// and answers only for the people inside the viewer's reach — decided on the server.
// ⚠️ The dashboard must NEVER call a report route — it has its own two
// (dashboardApi.getMyUpcomingLeave / getTeamTimeOff). Until 23/09/2026 it read a report
// route that sent every colleague's leave to every employee's browser (finding 9).
//
// `params` for the absence routes: { year } for a leave year, or { start, end } (ISO) for
// chosen dates, plus optional department_id, location_id, leave_type (a label), month.
export const reportsApi = {
  overview: (year) =>
    createApiClient().get('/reports/overview', { params: { year } }),
  absence: (params) =>
    createApiClient().get('/reports/absence-over-time', { params }),
  absencePeople: (params) =>
    createApiClient().get('/reports/absence-over-time/people', { params }),
  // A download, not a link — it needs the Authorization header (see payrollApi.exportReport).
  exportAbsence: (params) =>
    createApiClient().get('/reports/absence-over-time/export', { params, responseType: 'blob' }),
  // What we owe (Reports step 2, 09/10/2026) — always as of today, this leave year.
  owe: (params) =>
    createApiClient().get('/reports/what-we-owe', { params }),
  owePerson: (userId) =>
    createApiClient().get(`/reports/what-we-owe/person/${userId}`),
  exportOwe: (params) =>
    createApiClient().get('/reports/what-we-owe/export', { params, responseType: 'blob' }),
  // Managers keeping up (Reports step 3, 12/10/2026).
  keepingUp: (params) =>
    createApiClient().get('/reports/managers-keeping-up', { params }),
  keepingUpApprover: (params) =>
    createApiClient().get('/reports/managers-keeping-up/approver', { params }),
  exportKeepingUp: (params) =>
    createApiClient().get('/reports/managers-keeping-up/export', { params, responseType: 'blob' }),
  // Sickness (Reports step 4, 12/10/2026) — ⚠️ health data; 403 without "View sickness".
  sickness: (params) =>
    createApiClient().get('/reports/sickness', { params }),
  sicknessPerson: (userId, params) =>
    createApiClient().get(`/reports/sickness/person/${userId}`, { params }),
  sicknessWeekday: (weekday, params) =>
    createApiClient().get(`/reports/sickness/weekday/${weekday}`, { params }),
  sicknessMonth: (month, params) =>
    createApiClient().get(`/reports/sickness/month/${month}`, { params }),
  exportSickness: (params) =>
    createApiClient().get('/reports/sickness/export', { params, responseType: 'blob' }),
  // Getting a break (Reports step 4) — as of today.
  breaks: (params) =>
    createApiClient().get('/reports/getting-a-break', { params }),
  breaksPerson: (userId) =>
    createApiClient().get(`/reports/getting-a-break/person/${userId}`),
  exportBreaks: (params) =>
    createApiClient().get('/reports/getting-a-break/export', { params, responseType: 'blob' }),
};

// Payroll API (DESIGN-payroll.md stage 2a).
// Every one of these 403s for anyone without the payroll permission — the page
// hiding itself is a convenience, not the protection.
export const payrollApi = {
  getPeriod: (offset = 0) =>
    createApiClient().get('/payroll/period', { params: { offset } }),
  getReport: ({ offset = 0, start, end, includeEveryone = false } = {}) =>
    createApiClient().get('/payroll/report', {
      params: { offset, start, end, include_everyone: includeEveryone },
    }),

  // ⚠️ A DOWNLOAD, NOT A LINK. The endpoint needs the Authorization header, so a
  // plain <a href> would arrive unauthenticated and 401 — it has to come back as
  // a blob and be saved by script. `responseType: 'blob'` is what stops axios
  // parsing an .xlsx as text and corrupting it.
  exportReport: ({ format = 'xlsx', offset = 0, start, end, includeEveryone = false } = {}) =>
    createApiClient().get('/payroll/export', {
      params: { format, offset, start, end, include_everyone: includeEveryone },
      responseType: 'blob',
    }),
};

/**
 * Save a blob response as a file, using the filename the server chose.
 *
 * The server sets Content-Disposition (and exposes it via CORS) so the period
 * ends up in the filename — a clerk with twelve of these in a folder needs to
 * tell them apart without opening any.
 */
export const saveBlobResponse = (response, fallbackName) => {
  const disposition = response?.headers?.['content-disposition'] || '';
  const match = /filename="?([^"]+)"?/.exec(disposition);
  const name = (match && match[1]) || fallbackName;
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Let the click start before revoking, or the download never begins.
  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
  return name;
};

// Settings API
export const settingsApi = {
  get: () => 
    createApiClient().get('/settings'),
  update: (data) => 
    createApiClient().put('/settings', data),
  // Who a change to the carry-over expiry would reach this leave year (the Save dialog).
  carryExpiryImpact: (months) =>
    createApiClient().get('/settings/carry-over-expiry/impact', { params: { months } }),
  getOptions: () => 
    createApiClient().get('/settings/options'),
  // Business Hours
  getBusinessHours: () =>
    createApiClient().get('/settings/business-hours'),
  updateBusinessHours: (data) =>
    createApiClient().put('/settings/business-hours', data),
  // Advisory check on a working-hours pattern: derived weekly hours + statutory
  // rest-break warnings. The law lives server-side (services/jurisdictions.py) —
  // never hard-code it in the frontend.
  checkWorkingHours: (data) =>
    createApiClient().post('/settings/working-hours/check', data),
  // The tenant's employment-law facts (or all-nulls when their country isn't
  // modelled). The law lives server-side — never hard-code it in the frontend.
  getJurisdiction: () =>
    createApiClient().get('/settings/jurisdiction'),
  // Manager Settings
  getManagerSettings: () =>
    createApiClient().get('/settings/manager'),
  updateManagerSettings: (data) =>
    createApiClient().put('/settings/manager', data),
  managersFollowGlobal: (userIds) =>
    createApiClient().post('/settings/manager/follow-global', { user_ids: userIds }),
  // Settings → Security (admin only): 2FA requirements, sign-in code switches,
  // and per-group adoption counts.
  getSecuritySettings: () =>
    createApiClient().get('/settings/security'),
  updateSecuritySettings: (data) =>
    createApiClient().put('/settings/security', data),
};

// Advisory statutory entitlement (admin only). jurisdiction: null in the
// response = country not modelled = render NOTHING (D9), never UK numbers.
export const entitlementApi = {
  advisory: (year) =>
    createApiClient().get('/entitlement/advisory', { params: { year } }),
  // `annualAllowance` (optional): answer for the figure being TYPED — nothing is saved.
  advisoryUser: (userId, year, annualAllowance) =>
    createApiClient().get(`/entitlement/advisory/${userId}`, {
      params: { year, annual_allowance: annualAllowance } }),
};

// Blackouts API
export const blackoutsApi = {
  list: (includeInactive = false) => 
    createApiClient().get('/blackouts', { params: { include_inactive: includeInactive } }),
  create: (data) => 
    createApiClient().post('/blackouts', data),
  update: (id, data) => 
    createApiClient().put(`/blackouts/${id}`, data),
  delete: (id) => 
    createApiClient().delete(`/blackouts/${id}`),
  // What THIS person may pick in the No-leave dates box — decided by the save's own rule (02/11/2026).
  options: () =>
    createApiClient().get('/blackouts/options'),
};

// Public Holidays API
export const holidaysApi = {
  list: (year, userId) => 
    createApiClient().get('/holidays', { params: { year, user_id: userId } }),
  create: (data) => 
    createApiClient().post('/holidays', data),
  // Deletes a company's OWN day; records an opt-out for a shared bank holiday. The
  // response says which happened — see routers/holidays.delete_holiday.
  delete: (id) =>
    createApiClient().delete(`/holidays/${id}`),
  listOptedOut: () =>
    createApiClient().get('/holidays/opted-out'),
  // The January review: has anybody checked this year's dates, and what would they be
  // confirming? See backend/services/holiday_review.py.
  getReview: () =>
    createApiClient().get('/holidays/review'),
  confirmReview: () =>
    createApiClient().post('/holidays/review/confirm'),
  // Countries we can supply holidays for, plus which have selectable regions.
  getCountries: () =>
    createApiClient().get('/holidays/countries'),
  restore: (id) =>
    createApiClient().post(`/holidays/${id}/restore`),
  getUKBankHolidays: (region, year) => 
    createApiClient().get('/holidays/uk-bank-holidays', { params: { region, year } }),
  importUK: (region, year) => 
    createApiClient().post('/holidays/import-uk', null, { params: { region, year } }),
  getRegions: () => 
    createApiClient().get('/holidays/regions'),
};

// Profile API
export const profileApi = {
  get: () => 
    createApiClient().get('/profile'),
  update: (data) => 
    createApiClient().put('/profile', data),
  changePassword: (data) => 
    createApiClient().post('/profile/change-password', data),
  getAllowances: (year) =>
    createApiClient().get('/profile/allowances', { params: { year } }),
  // Calendar feeds (ROADMAP §7). The GET issues a key the first time it is called, so
  // opening the profile page is what creates somebody's links — nobody has to ask.
  getCalendarFeed: () =>
    createApiClient().get('/profile/calendar-feed'),
  resetCalendarFeed: () =>
    createApiClient().post('/profile/calendar-feed/reset'),
  // Where this person's leave notifications go, plus whether Teams can actually reach
  // them. ⚠️ `teams_installed` comes from the server because the browser has no way to
  // know whether a TeamsInstall row exists — and offering Teams to somebody who has not
  // added the app would let them choose silence. Saving goes through `update` above,
  // which already accepts `notification_channel`.
  getNotifications: () =>
    createApiClient().get('/profile/notifications'),
  // The one-off "Your company uses Slack" bar (Slack build step 2, decision H3a). The server
  // decides whether it shows; answering either way means it never shows again.
  getSlackOffer: () =>
    createApiClient().get('/profile/slack-offer'),
  answerSlackOffer: (answer) =>
    createApiClient().post('/profile/slack-offer', { answer }),
};

// Calendar connections — the OAuth half of calendars, which WRITES leave into somebody's
// own calendar rather than publishing a file for their calendar app to poll.
//
// ⚠️ `startMicrosoft` DELIBERATELY DOES NOT NAVIGATE. It is an authenticated POST that
// returns a URL, and the caller sets `window.location.href` itself. The obvious
// alternative — a plain `<a href>` like the "Continue with Microsoft" sign-in button — is
// wrong here: a link carries no bearer token, so the backend would have no idea whose
// calendar was being connected. See the docstring on `routers/calendar_connect.py`.
//
// ⚠️ `list` RETURNS EVERY PROVIDER, INCLUDING UNCONFIGURED ONES. The card decides what to
// render from each row's `available` flag. Filtering here would make "Google isn't set up
// on this installation" indistinguishable from "Google doesn't exist", and the card could
// never explain the difference to an administrator wondering where the button went.
export const calendarConnectionApi = {
  list: () =>
    createApiClient().get('/calendar/connections'),
  start: (provider) =>
    createApiClient().post(`/calendar/${provider}/connect`),
  disconnect: (provider) =>
    createApiClient().post(`/calendar/${provider}/disconnect`),
};

// Microsoft Teams — the company-level half (Settings → Integrations). The per-person half
// lives on the profile and goes through `profileApi` above.
//
// ⚠️ `downloadPackage` asks for a BLOB. Without `responseType: 'blob'` axios parses the zip
// as text and the saved file is corrupt in a way that only shows up when Teams rejects it
// with "we couldn't parse the app manifest" — which is the least helpful error in this
// whole feature and would look like our manifest was wrong.
// ⚠️ Sends a digest for real, right now, to everyone it would normally reach — Rob's
// decision (b), 26/08/2026 — and deliberately does NOT write a dedup row, so it cannot
// cancel that day's scheduled send.
export const digestTestApi = {
  send: (kind) =>
    createApiClient().post(`/settings/notifications/test?kind=${encodeURIComponent(kind)}`),
};

export const teamsApi = {
  status: () =>
    createApiClient().get('/teams/status'),
  // Only channels the app is actually installed in — nobody types a channel id, and
  // nowhere unreachable can be chosen.
  channels: () =>
    createApiClient().get('/teams/channels'),
  downloadPackage: () =>
    createApiClient().get('/teams/app-package', { responseType: 'blob' }),
};

// Slack — the company half (Settings → Integrations), build step 1 of DESIGN-slack.md.
//
// ⚠️ `connect` is a POST that answers with Slack's address; the card then navigates there
// itself. It is NOT a link: the server ties the returning install to the admin who asked
// (routers/slack.py), so a forged URL can never attach a stranger's Slack to this company.
export const slackApi = {
  status: () =>
    createApiClient().get('/slack/status'),
  connect: () =>
    createApiClient().post('/slack/connect'),
  rematch: () =>
    createApiClient().post('/slack/rematch'),
  members: () =>
    createApiClient().get('/slack/members'),
  link: (userId, slackUserId) =>
    createApiClient().post('/slack/link', { user_id: userId, slack_user_id: slackUserId }),
  // Undo a link made by hand (Rob, 15/10/2026). Links found by email can't be unlinked.
  unlink: (userId) =>
    createApiClient().post('/slack/unlink', { user_id: userId }),
  // The admin's switch for staff who can't sign in (no email address) — decision H3b.
  setNotifications: (userId, on) =>
    createApiClient().post(`/slack/people/${userId}/notifications`, { on }),
  // Build step 4: the channels the who's-off post can go in, and the one chosen now. The
  // choice itself is saved with the rest of Settings → Notifications (`slack_channel_id`).
  channels: () =>
    createApiClient().get('/slack/channels'),
  disconnect: () =>
    createApiClient().post('/slack/disconnect'),
};

// Superadmin API
// Approval groups — a named set of approvers, selectable anywhere a single person can
// be. Tenant-level admin config, NOT superadmin: it lives under the approval-policies
// router because that's the same mental space (Settings -> Approval Flow).
export const approvalPoliciesApi = {
  listApprovalGroups: () =>
    createApiClient().get('/approval-groups'),
  createApprovalGroup: (payload) =>
    createApiClient().post('/approval-groups', payload),
  updateApprovalGroup: (id, payload) =>
    createApiClient().put(`/approval-groups/${id}`, payload),
  deleteApprovalGroup: (id) =>
    createApiClient().delete(`/approval-groups/${id}`),
};

export const superadminApi = {
  getStats: () => 
    createApiClient().get('/superadmin/stats'),
  // The pre-launch "register your interest" list (12/09/2026) — Superadmin → Interest list.
  listInterest: () =>
    createApiClient().get('/superadmin/interest'),
  setInterestContacted: (id, contacted) =>
    createApiClient().patch(`/superadmin/interest/${id}`, { contacted }),
  listTenants: (params) =>
    createApiClient().get('/superadmin/tenants', { params }),
  // Sign-in providers — Superadmin → API Keys. ⚠️ `source` tells the screen whether the
  // keys in force came from the database, the .env file, or nowhere; without it a stale
  // .env value looks identical to a saved one.
  getSsoConfig: () =>
    createApiClient().get('/superadmin/sso/config'),
  saveSsoConfig: (payload) =>
    createApiClient().put('/superadmin/sso/config', payload),
  revealSsoSecret: (provider) =>
    createApiClient().post('/superadmin/sso/config/reveal', { provider }),
  testSsoCredentials: (provider) =>
    createApiClient().post('/superadmin/sso/config/test', { provider }),
  getTenant: (id) => 
    createApiClient().get(`/superadmin/tenants/${id}`),
  updateTenant: (id, data) =>
    createApiClient().put(`/superadmin/tenants/${id}`, data),
  actionTenant: (id, action, extra = {}) =>
    createApiClient().post(`/superadmin/tenants/${id}/action`, { action, ...extra }),
  // Phase D: full customer backup (xlsx) — delete-flow link + standalone tool
  exportTenant: (id) =>
    createApiClient().get(`/superadmin/tenants/${id}/export`, { responseType: 'blob' }),
  getTenantUsers: (id) =>
    createApiClient().get(`/superadmin/tenants/${id}/users`),
  // Break-glass 2FA reset (G5) — for somebody who has lost both their second factor
  // and their recovery codes. `confirmation` is their email (or full name if they
  // have none) typed back, same friction as purging a company.
  resetUserTwoFactor: (tenantId, userId, confirmation) =>
    createApiClient().post(
      `/superadmin/tenants/${tenantId}/users/${userId}/reset-two-factor`,
      { confirmation }),
  // Trial management
  extendTrial: (id, days) =>
    createApiClient().post(`/superadmin/tenants/${id}/extend-trial`, { days }),
  // Trials panel: table of companies on trial + the configurable trial length.
  getTrials: () =>
    createApiClient().get('/superadmin/trials'),
  // Sign-ups refused as bots in the last 30 days, by reason (Group A7).
  getSignupBlocks: () =>
    createApiClient().get('/superadmin/signup-blocks'),
  updateTrialSettings: (trialPeriodDays) =>
    createApiClient().put('/superadmin/trial-settings', { trial_period_days: trialPeriodDays }),

  // Approval reminder ladder — platform-wide, deliberately NOT per customer.
  // Where a customer's leave requests actually went, and who can act on them.
  // Tenant-scoped so a customer-facing version is a small follow-on (Rob's B2).
  // Public holidays are held once for the whole platform — see services/holidays_util.
  getPublicHolidayStatus: () =>
    createApiClient().get('/superadmin/public-holidays'),
  addPublicHoliday: (data) =>
    createApiClient().post('/superadmin/public-holidays', data),
  removePublicHoliday: (id) =>
    createApiClient().delete(`/superadmin/public-holidays/${id}`),
  refreshPublicHolidays: () =>
    createApiClient().post('/superadmin/public-holidays/refresh'),
  getRequestRouting: (params) =>
    createApiClient().get('/superadmin/request-routing', { params }),
  // Read-only: the law figures the advice uses, when checked, and where in the code.
  getEmploymentLaw: () =>
    createApiClient().get('/superadmin/employment-law'),
  getApprovalReminders: () =>
    createApiClient().get('/superadmin/approval-reminders'),
  updateApprovalReminders: (payload) =>
    createApiClient().put('/superadmin/approval-reminders', payload),
  endTrial: (id) =>
    createApiClient().post(`/superadmin/tenants/${id}/end-trial`),
  resyncBilling: (id) =>
    createApiClient().post(`/superadmin/tenants/${id}/resync-billing`),
  getInvoiceLink: (tenantId, invoiceId) =>
    createApiClient().get(`/superadmin/tenants/${tenantId}/invoices/${invoiceId}/link`),
  convertToPaid: (id, plan = 'monthly') =>
    createApiClient().post(`/superadmin/tenants/${id}/convert-to-paid`, { plan }),
  // Activity log
  getActivityLog: (params) =>
    createApiClient().get('/superadmin/activity-log', { params }),
  // CRM endpoints
  getCrmOverview: () =>
    createApiClient().get('/superadmin/crm/stats/overview'),
  getCrmPipeline: () =>
    createApiClient().get('/superadmin/crm/pipeline'),
  listChurnedCustomers: () =>
    createApiClient().get('/superadmin/crm/churned'),
  listCustomers: (params) =>
    createApiClient().get('/superadmin/crm/customers', { params }),
  getCustomerDetail: (id) =>
    createApiClient().get(`/superadmin/crm/customers/${id}`),
  // Sales CRM notes + follow-up (17 Jul)
  listCustomerNotes: (id) =>
    createApiClient().get(`/superadmin/crm/customers/${id}/notes`),
  addCustomerNote: (id, content) =>
    createApiClient().post(`/superadmin/crm/customers/${id}/notes`, { content }),
  deleteCustomerNote: (id, noteId) =>
    createApiClient().delete(`/superadmin/crm/customers/${id}/notes/${noteId}`),
  setCustomerFollowUp: (id, followUpAt) =>
    createApiClient().put(`/superadmin/crm/customers/${id}/follow-up`, { follow_up_at: followUpAt }),
  setDoNotContact: (id, doNotContact) =>
    createApiClient().put(`/superadmin/crm/customers/${id}/do-not-contact`, { do_not_contact: doNotContact }),
  getDueFollowUps: () =>
    createApiClient().get('/superadmin/crm/follow-ups'),
  listSupportTickets: (status) =>
    createApiClient().get('/superadmin/support/tickets', { params: status ? { status } : {} }),
  getSupportTicket: (id) =>
    createApiClient().get(`/superadmin/support/tickets/${id}`),
  replySupportTicket: (id, body, files = []) => {
    const fd = new FormData();
    fd.append('body', body || '');
    (files || []).forEach((f) => fd.append('files', f));
    return createApiClient().post(`/superadmin/support/tickets/${id}/messages`, fd);   // browser sets the multipart boundary
  },
  // Same authenticated download route as the customer side (it allows superadmin).
  getSupportAttachment: (id) =>
    createApiClient().get(`/support/attachments/${id}`, { responseType: 'blob' }),
  setSupportTicketStatus: (id, status) =>
    createApiClient().put(`/superadmin/support/tickets/${id}/status`, { status }),
  impersonate: (ticketId) =>
    createApiClient().post('/superadmin/support/impersonate', { ticket_id: ticketId }),
  impersonateEnd: (ticketId) =>
    createApiClient().post('/superadmin/support/impersonate/end', { ticket_id: ticketId }),
  revokeSupportAccess: (id) =>
    createApiClient().post(`/superadmin/support/tickets/${id}/revoke-access`),
  // In-app toast message overrides (Superadmin → Messages editor)
  listUiMessageOverrides: () => createApiClient().get('/ui-messages'),
  saveUiMessage: (key, text) => createApiClient().put('/ui-messages', { key, text }),
  listTeam: () => createApiClient().get('/superadmin/team'),
  listTeamCapabilities: () => createApiClient().get('/superadmin/team/capabilities'),
  createTeamMember: (data) => createApiClient().post('/superadmin/team', data),
  updateTeamMember: (id, data) => createApiClient().put(`/superadmin/team/${id}`, data),
  deleteTeamMember: (id) => createApiClient().delete(`/superadmin/team/${id}`),
  // System settings (HolidayHive company details)
  getSystemSettings: () =>
    createApiClient().get('/superadmin/system-settings'),
  updateSystemSettings: (data) =>
    createApiClient().put('/superadmin/system-settings', data),
  // Data export
  exportFullDatabase: () =>
    createApiClient().get('/superadmin/export/full', { responseType: 'blob' }),
  exportTenantData: (tenantId) =>
    createApiClient().get(`/superadmin/export/tenant/${tenantId}`, { responseType: 'blob' }),
  // Platform settings
  getPlatformSettings: () =>
    createApiClient().get('/superadmin/platform-settings'),
  updatePlatformSetting: (key, value) =>
    createApiClient().put(`/superadmin/platform-settings/${key}`, { value }),
  // Address API key (available to all users)
  getAddressApiKey: () =>
    createApiClient().get('/superadmin/address-api-key'),
  // Payment provider config (Stripe + GoCardless) — superadmin only
  getPaymentsConfig: () =>
    createApiClient().get('/superadmin/payments/config'),
  revealPaymentsKey: (provider, mode, field) =>
    createApiClient().post('/superadmin/payments/config/reveal', { provider, mode, field }),
  savePaymentsConfig: (data) =>
    createApiClient().put('/superadmin/payments/config', data),
  testPaymentsConnection: () =>
    createApiClient().post('/superadmin/payments/config/test'),
  syncStripePlans: () =>
    createApiClient().post('/superadmin/payments/stripe/sync-plans'),
  // Leave-year rollover scheduler monitoring
  getRolloverStatus: () =>
    createApiClient().get('/superadmin/rollover/status'),
  getRolloverRuns: (limit = 100) =>
    createApiClient().get('/superadmin/rollover/runs', { params: { limit } }),
  runRolloverNow: () =>
    createApiClient().post('/superadmin/rollover/run-now'),
  // Scheduled notifications (digests + birthdays)
  getNotificationsStatus: () =>
    createApiClient().get('/superadmin/notifications/status'),
  getNotificationsSends: (limit = 150) =>
    createApiClient().get('/superadmin/notifications/sends', { params: { limit } }),
  runNotificationsNow: () =>
    createApiClient().post('/superadmin/notifications/run-now'),
};

// Onboarding API
export const leaveLimitsApi = {
  list: () => createApiClient().get('/leave-limits'),
  create: (data) => createApiClient().post('/leave-limits', data),
  delete: (id) => createApiClient().delete(`/leave-limits/${id}`),
  check: (data) => createApiClient().post('/leave-limits/check', data),
};

export const tagsApi = {
  list: () => createApiClient().get('/tags'),
  create: (data) => createApiClient().post('/tags', data),
  update: (id, data) => createApiClient().put(`/tags/${id}`, data),
  delete: (id) => createApiClient().delete(`/tags/${id}`),
  setUserTags: (userId, tagIds) => createApiClient().put(`/tags/user/${userId}`, { tag_ids: tagIds }),
};

export const onboardingApi = {
  getOptions: () =>
    createApiClient().get('/onboarding/options'),
  getStatus: () =>
    createApiClient().get('/onboarding/status'),
  updateCompanyInfo: (data) =>
    createApiClient().put('/onboarding/company-info', data),
  updateAdminInfo: (data) =>
    createApiClient().put('/onboarding/admin-info', data),
  updateLeavePolicy: (data) =>
    createApiClient().put('/onboarding/leave-policy', data),
  complete: () =>
    createApiClient().post('/onboarding/complete'),
  skip: () =>
    createApiClient().post('/onboarding/skip'),
};

// Billing API
export const billingApi = {
  getOverview: () =>
    createApiClient().get('/billing/overview'),
  // Phase 4: the trial/grace/block/dunning gate for the current tenant. Cheap,
  // safe for every authenticated user (drives the banner + paywall).
  subscriptionState: () =>
    createApiClient().get('/billing/subscription-state'),
  // Phase 5: open the Stripe Customer Portal (card / invoices / plan / cancel).
  createPortalSession: () =>
    createApiClient().post('/billing/portal'),
  // Reconcile this tenant's subscription with the provider — called on return
  // from the Customer Portal so a cancel / plan switch shows without webhook lag.
  resyncMyBilling: () =>
    createApiClient().post('/billing/resync'),
  // channel: 'card' (default) or 'bacs' for a Stripe Bacs Direct Debit checkout.
  createStripeCheckout: (planType, channel = 'card') =>
    createApiClient().post('/billing/checkout/stripe', { method: 'stripe', plan_type: planType, channel }),
  getCheckoutStatus: (sessionId) =>
    createApiClient().get(`/billing/checkout/status/${sessionId}`),
  setupGoCardless: (planType) =>
    createApiClient().post('/billing/setup/gocardless', { method: 'gocardless', plan_type: planType }),
  // Paying customer: schedule a plan change (monthly<->annual) for period end — no
  // proration. Backend keeps them on their current plan until then.
  switchPlan: (planType) =>
    createApiClient().post('/billing/switch-plan', { plan_type: planType }),
  // Undo a scheduled plan change before it takes effect (stay on current plan).
  cancelScheduledSwitch: () =>
    createApiClient().post('/billing/cancel-scheduled-switch'),
  confirmGoCardless: () =>
    createApiClient().post('/billing/gocardless/confirm'),
  // Change bank details for an existing Direct Debit customer (a fresh mandate).
  updateGoCardlessMandate: () =>
    createApiClient().post('/billing/gocardless/update-mandate'),
  getInvoices: () =>
    createApiClient().get('/billing/invoices'),
  getTransactions: () =>
    createApiClient().get('/billing/transactions'),
  updateInvoiceEmail: (email) =>
    createApiClient().put('/billing/invoice-email', { invoice_email: email }),
  downloadInvoice: (invoiceId) =>
    createApiClient().get(`/billing/invoices/${invoiceId}/download`, { responseType: 'blob' }),
  getInvoicePdfLink: (invoiceId) =>
    createApiClient().get(`/billing/invoices/${invoiceId}/pdf-link`),
  updatePaymentMethod: () =>
    createApiClient().post('/billing/update-payment-method'),
  // Self-service off-boarding (17 Jul). Admin-only on the backend.
  accountStatus: () =>
    createApiClient().get('/billing/account-status'),
  cancelPreview: () =>
    createApiClient().get('/billing/cancel-preview'),
  cancelAccount: (data) =>
    createApiClient().post('/billing/cancel', data),
  reinstateAccount: () =>
    createApiClient().post('/billing/reinstate'),
  // The customer's own full backup (same xlsx the superadmin gets).
  exportOwnData: () =>
    createApiClient().get('/billing/export', { responseType: 'blob' }),
};

export default {
  auth: authApi,
  email: emailApi,
  users: usersApi,
  departments: departmentsApi,
  locations: locationsApi,
  leave: leaveApi,
  dashboard: dashboardApi,
  rollover: rolloverApi,
  emailTemplates: emailTemplatesApi,
  reports: reportsApi,
  settings: settingsApi,
  blackouts: blackoutsApi,
  holidays: holidaysApi,
  profile: profileApi,
  calendarConnection: calendarConnectionApi,
  superadmin: superadminApi,
  leaveLimits: leaveLimitsApi,
  tags: tagsApi,
  onboarding: onboardingApi,
  billing: billingApi,
};

// Settings → Security and Profile → "Where you're signed in" (DESIGN-security.md, step 1).
// Settings → Integrations → API keys (DESIGN-api-and-webhooks.md step 1). Admins only.
// ⚠️ Only `make` ever returns a key, once, in `made.key`.
export const apiKeysApi = {
  list: () => createApiClient().get('/settings/api-keys'),
  make: (name, scopes) => createApiClient().post('/settings/api-keys', { name, scopes }),
  revoke: (id) => createApiClient().delete(`/settings/api-keys/${id}`),
};

// Settings → Integrations → Webhooks (DESIGN-api-and-webhooks.md step 2). Admins only.
// ⚠️ Only `add` ever returns the signing secret, once, in `made.secret`.
export const webhooksApi = {
  list: () => createApiClient().get('/settings/webhooks'),
  add: (url, events, showSickness) => createApiClient().post('/settings/webhooks',
    { url, events, show_sickness: showSickness }),
  change: (id, body) => createApiClient().patch(`/settings/webhooks/${id}`, body),
  remove: (id) => createApiClient().delete(`/settings/webhooks/${id}`),
  test: (id) => createApiClient().post(`/settings/webhooks/${id}/test`),
  resume: (id) => createApiClient().post(`/settings/webhooks/${id}/resume`),
  resend: (deliveryId) => createApiClient().post(`/settings/webhooks/deliveries/${deliveryId}/resend`),
};

export const securityApi = {
  sessions: () => createApiClient().get('/security/sessions'),
  signOutSession: (id) => createApiClient().post(`/security/sessions/${id}/sign-out`),
  signOutEveryone: () => createApiClient().post('/security/sign-out-everyone'),
  history: (params) => createApiClient().get('/security/events', { params }),
  exportHistory: (params) => createApiClient().get('/security/events/export', { params, responseType: 'blob' }),
  mine: () => createApiClient().get('/security/me'),
  signOutMySession: (id) => createApiClient().post(`/security/me/sessions/${id}/sign-out`),
  signOutMyOthers: () => createApiClient().post('/security/me/sign-out-others'),
  // Step 2 (25/10/2026): the health card on Overview. Admins only.
  health: () => createApiClient().get('/security/health'),
  remind: (userId) => createApiClient().post(`/security/health/remind/${userId}`),
  unlock: (userId) => createApiClient().post(`/security/health/unlock/${userId}`),
  // "It wasn't me", from the "Was this you?" email — NOT signed in, so plain axios. The token
  // goes in the body. ⚠️ `check` changes nothing (mail scanners open links on their own);
  // only `confirm` signs anybody out.
  notMeCheck: (token) => axios.post(`${API_URL}/api/security/not-me/check`, { token }),
  notMeConfirm: (token) => axios.post(`${API_URL}/api/security/not-me/confirm`, { token }),
};

